import { getGetBatteriesUrl } from 'generatedTypes/asset/services/battery-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { BatteryResponse, FetchBatteries, FetchBatteriesParams } from './types';

const useBatteries = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchBatteries: FetchBatteries = async request => {
        try {
            setIsLoading(true);
            const url = getGetBatteriesUrl(request);

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchBatteries(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: batteriesData,
        fetch: paginatedFetch,
        reset
    } = usePagination<BatteryResponse>({
        callback: fetchBatteries
    });

    const handleFetch = (args?: FetchBatteriesParams) => {
        const { name, pageSize = DEFAULT_PAGE_SIZE, sort } = args || {};

        return paginatedFetch({
            name,
            pageSize,
            sort
        });
    };

    return {
        batteries: batteriesData as BatteryResponse[],
        fetch: handleFetch,
        isLoading,
        reset
    };
};

export default useBatteries;
